import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationCircleIcon, CheckCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/apiConfig';
import { Tool } from '../../types/tool';
import { Project } from '../../types/project';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';

interface ToolFormProps {
  isOpen: boolean;
  onClose: () => void;
  onToolCreated: (tool: Tool) => void;
  initialTool?: Tool | null;
  isEditMode?: boolean;
}

interface ToolFormData {
  name: string;
  description: string;
  status: 'active' | 'paused' | 'retired' | 'planned';
  tool_type: 'software' | 'outsourcing' | 'other';
  url_or_path: string;
  related_project_id: string;
  is_internal: boolean;
  pending_review: boolean;
}

const initialFormData: ToolFormData = {
  name: '',
  description: '',
  status: 'active',
  tool_type: 'other',
  url_or_path: '',
  related_project_id: '',
  is_internal: false,
  pending_review: false,
};

const ToolForm: React.FC<ToolFormProps> = ({
    isOpen,
    onClose,
    onToolCreated,
    initialTool = null,
    isEditMode = false
}) => {
  const [formData, setFormData] = useState<ToolFormData>(initialFormData);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);

  // Fetch projects for the dropdown
  useEffect(() => {
    const fetchProjects = async () => {
      if (!isOpen) return;
      setIsLoadingProjects(true);
      try {
        const response = await apiClient.get('/api/projects/');
        setProjects(response.data || []);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setIsLoadingProjects(false);
      }
    };
    fetchProjects();
  }, [isOpen]);

  // Reset form when modal opens/closes or initial data changes
  useEffect(() => {
    if (isOpen) {
      if (isEditMode && initialTool) {
        setFormData({
          name: initialTool.name || '',
          description: initialTool.description || '',
          status: initialTool.status || 'active',
          tool_type: initialTool.tool_type || 'other',
          url_or_path: initialTool.url_or_path || '',
          related_project_id: initialTool.related_project?.id?.toString() || '',
          is_internal: initialTool.is_internal || false,
          pending_review: initialTool.pending_review || false,
        });
      } else {
        setFormData(initialFormData);
      }
    }
    if (!isOpen) {
      setTimeout(() => {
        setSubmitError(null);
        setSubmitSuccess(false);
      }, 300)
    }
  }, [isOpen, isEditMode, initialTool]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    const payload: any = {
      name: formData.name,
      description: formData.description || '',
      status: formData.status,
      tool_type: formData.tool_type,
      url_or_path: formData.url_or_path || '',
      is_internal: formData.is_internal,
      pending_review: formData.pending_review,
    };

    // Only include related_project if a project is selected
    if (formData.related_project_id) {
      payload.related_project = parseInt(formData.related_project_id);
    } else {
      payload.related_project = null;
    }

    try {
      let response;
      if (isEditMode && initialTool?.id) {
        response = await apiClient.patch(`/api/tools/${initialTool.id}/`, payload);
      } else {
        response = await apiClient.post('/api/tools/', payload);
      }

      if (response.status >= 200 && response.status < 300) {
        setSubmitSuccess(true);
        onToolCreated(response.data);
        if (!isEditMode) {
          setFormData(initialFormData);
        }
        setTimeout(() => { onClose(); }, 1500);
      } else {
        throw new Error(`Failed to ${isEditMode ? 'update' : 'create'} tool (${response.status})`);
      }
    } catch (err: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} tool:`, err);
      const errorMsg = err.message || `Failed to ${isEditMode ? 'update' : 'create'} tool.`;
      setSubmitError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = useCallback(() => {
    if (!isSubmitting) { onClose(); }
  }, [isSubmitting, onClose]);

  // Styles
  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
  };

  const scrollContainerStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    overflowY: 'auto',
  };

  const centerContainerStyle: React.CSSProperties = {
    display: 'flex',
    minHeight: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    padding: SPACING[16],
    textAlign: 'center',
  };

  const panelStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: '28rem',
    transform: 'none',
    overflow: 'hidden',
    borderRadius: '16px',
    backgroundColor: COLORS.bg.panel,
    padding: SPACING[24],
    textAlign: 'left',
    verticalAlign: 'middle',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    transition: 'all 0.3s ease',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: SPACING[16],
  };

  const titleStyle: React.CSSProperties = {
    fontSize: TYPOGRAPHY.size.lg,
    fontWeight: TYPOGRAPHY.weight.medium,
    color: COLORS.text.primary,
  };

  const closeButtonStyle: React.CSSProperties = {
    color: COLORS.text.tertiary,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    opacity: isSubmitting ? 0.5 : 1,
  };

  const formStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING[16],
  };

  const fieldGroupStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: TYPOGRAPHY.size.sm,
    fontWeight: TYPOGRAPHY.weight.medium,
    color: COLORS.text.secondary,
    marginBottom: SPACING[4],
  };

  const inputStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    borderRadius: RADIUS,
    border: `1px solid ${COLORS.border.default}`,
    padding: `${SPACING[8]} ${SPACING[12]}`,
    fontSize: TYPOGRAPHY.size.sm,
    fontFamily: TYPOGRAPHY.body.fontFamily,
    backgroundColor: COLORS.bg.panel,
    color: COLORS.text.primary,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const textareaStyle: React.CSSProperties = {
    ...inputStyle,
    resize: 'vertical',
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    cursor: 'pointer',
  };

  const checkboxContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: SPACING[24],
  };

  const checkboxGroupStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
  };

  const checkboxStyle: React.CSSProperties = {
    width: '16px',
    height: '16px',
    accentColor: COLORS.accent.default,
    borderRadius: '4px',
    cursor: 'pointer',
  };

  const checkboxLabelStyle: React.CSSProperties = {
    marginLeft: SPACING[8],
    display: 'block',
    fontSize: TYPOGRAPHY.size.sm,
    color: COLORS.text.secondary,
  };

  const footerStyle: React.CSSProperties = {
    marginTop: SPACING[24],
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING[12],
  };

  const successMessageStyle: React.CSSProperties = {
    padding: SPACING[12],
    fontSize: TYPOGRAPHY.size.sm,
    color: COLORS.status.success,
    backgroundColor: '#F0FDF4',
    borderRadius: RADIUS,
    border: `1px solid ${COLORS.status.success}`,
    display: 'flex',
    alignItems: 'center',
  };

  const errorMessageStyle: React.CSSProperties = {
    padding: SPACING[12],
    fontSize: TYPOGRAPHY.size.sm,
    color: COLORS.status.error,
    backgroundColor: '#FEF2F2',
    borderRadius: RADIUS,
    border: `1px solid ${COLORS.status.error}`,
    display: 'flex',
    alignItems: 'center',
  };

  const buttonContainerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: SPACING[12],
  };

  const cancelButtonStyle: React.CSSProperties = {
    display: 'inline-flex',
    justifyContent: 'center',
    borderRadius: RADIUS,
    border: `1px solid ${COLORS.border.default}`,
    backgroundColor: COLORS.bg.panel,
    padding: `${SPACING[8]} ${SPACING[16]}`,
    fontSize: TYPOGRAPHY.size.sm,
    fontWeight: TYPOGRAPHY.weight.medium,
    color: COLORS.text.secondary,
    cursor: 'pointer',
    opacity: isSubmitting ? 0.5 : 1,
  };

  const submitButtonStyle: React.CSSProperties = {
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: RADIUS,
    border: 'none',
    backgroundColor: COLORS.accent.default,
    padding: `${SPACING[8]} ${SPACING[16]}`,
    fontSize: TYPOGRAPHY.size.sm,
    fontWeight: TYPOGRAPHY.weight.medium,
    color: COLORS.bg.panel,
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    opacity: isSubmitting ? 0.5 : 1,
  };

  const iconStyle: React.CSSProperties = {
    width: '24px',
    height: '24px',
  };

  const smallIconStyle: React.CSSProperties = {
    width: '20px',
    height: '20px',
    marginRight: SPACING[8],
  };

  const spinnerIconStyle: React.CSSProperties = {
    width: '16px',
    height: '16px',
    marginRight: SPACING[8],
    animation: 'spin 1s linear infinite',
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" style={{ position: 'relative', zIndex: 10 }} onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div style={overlayStyle} />
        </Transition.Child>

        <div style={scrollContainerStyle}>
          <div style={centerContainerStyle}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel style={panelStyle}>
                <div style={headerStyle}>
                  <Dialog.Title as="h3" style={titleStyle}>
                    {isEditMode ? 'Edit Tool' : 'Add New Tool'}
                  </Dialog.Title>
                  <button
                    type="button"
                    style={closeButtonStyle}
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    <XMarkIcon style={iconStyle} />
                  </button>
                </div>

                <form onSubmit={handleSubmit} style={formStyle}>
                  <div style={fieldGroupStyle}>
                    <label htmlFor="name" style={labelStyle}>
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      style={inputStyle}
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div style={fieldGroupStyle}>
                    <label htmlFor="description" style={labelStyle}>
                      Description
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      rows={3}
                      style={textareaStyle}
                      value={formData.description}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div style={fieldGroupStyle}>
                    <label htmlFor="status" style={labelStyle}>
                      Status
                    </label>
                    <select
                      id="status"
                      name="status"
                      style={selectStyle}
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      <option value="active">Active</option>
                      <option value="paused">Paused</option>
                      <option value="retired">Retired</option>
                      <option value="planned">Planned</option>
                    </select>
                  </div>

                  <div style={fieldGroupStyle}>
                    <label htmlFor="tool_type" style={labelStyle}>
                      Tool Type
                    </label>
                    <select
                      id="tool_type"
                      name="tool_type"
                      style={selectStyle}
                      value={formData.tool_type}
                      onChange={handleInputChange}
                    >
                      <option value="software">Software</option>
                      <option value="outsourcing">Outsourcing</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div style={fieldGroupStyle}>
                    <label htmlFor="url_or_path" style={labelStyle}>
                      URL or Path
                    </label>
                    <input
                      type="text"
                      id="url_or_path"
                      name="url_or_path"
                      style={inputStyle}
                      value={formData.url_or_path}
                      onChange={handleInputChange}
                      placeholder="e.g., https://example.com or /path/to/tool"
                    />
                  </div>

                  <div style={fieldGroupStyle}>
                    <label htmlFor="related_project_id" style={labelStyle}>
                      Related Project
                    </label>
                    <select
                      id="related_project_id"
                      name="related_project_id"
                      style={{ ...selectStyle, opacity: isLoadingProjects ? 0.5 : 1 }}
                      value={formData.related_project_id}
                      onChange={handleInputChange}
                      disabled={isLoadingProjects}
                    >
                      <option value="">Select a project (optional)</option>
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={checkboxContainerStyle}>
                    <div style={checkboxGroupStyle}>
                      <input
                        type="checkbox"
                        id="is_internal"
                        name="is_internal"
                        style={checkboxStyle}
                        checked={formData.is_internal}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="is_internal" style={checkboxLabelStyle}>
                        Internal Tool
                      </label>
                    </div>

                    <div style={checkboxGroupStyle}>
                      <input
                        type="checkbox"
                        id="pending_review"
                        name="pending_review"
                        style={checkboxStyle}
                        checked={formData.pending_review}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="pending_review" style={checkboxLabelStyle}>
                        Pending Review
                      </label>
                    </div>
                  </div>

                  <div style={footerStyle}>
                    {submitSuccess && (
                      <div style={successMessageStyle}>
                        <CheckCircleIcon style={smallIconStyle} />
                        Tool {isEditMode ? 'updated' : 'created'}!
                      </div>
                    )}
                    {submitError && (
                      <div style={errorMessageStyle}>
                        <ExclamationCircleIcon style={smallIconStyle} />
                        {submitError}
                      </div>
                    )}
                    <div style={buttonContainerStyle}>
                      <button
                        type="button"
                        style={cancelButtonStyle}
                        onClick={handleClose}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        style={submitButtonStyle}
                        disabled={isSubmitting}
                      >
                        {isSubmitting && <ArrowPathIcon style={spinnerIconStyle} />}
                        {isSubmitting ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Tool' : 'Create Tool')}
                      </button>
                    </div>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ToolForm;
